import * as React from 'react';

import {
  ArrowDown,
  ArrowUp,
  Bot,
  ChartNoAxesCombined,
  ChevronDown,
  ChevronsUpDown,
  Copy,
  ExternalLink,
  GripVertical,
  Images,
  Info,
  KeyRound,
  ListChecks,
  ListFilter,
  Lock,
  Plus,
  Search as SearchIcon,
  Settings2,
  Share2,
  ThumbsDown,
  ThumbsUp,
  Trash2
} from 'lucide-react';

import type { LucideIcon } from 'lucide-react';

type CompatibilityIconProps = React.ComponentPropsWithoutRef<'svg'> & {
  /** Temporary compatibility for call sites that still pass a legacy `sx`-style icon override object. */
  sx?: unknown;
};

const toCssStyle = (sx: unknown): React.CSSProperties | undefined => {
  if (!sx || typeof sx !== 'object' || Array.isArray(sx)) {
    return undefined;
  }

  const isCssValue = (value: unknown): value is string | number =>
    typeof value === 'string' || typeof value === 'number';
  const fontSize = 'fontSize' in sx ? sx.fontSize : undefined;
  const width = 'width' in sx ? sx.width : undefined;
  const height = 'height' in sx ? sx.height : undefined;

  return {
    ...(isCssValue(fontSize) ? { fontSize } : {}),
    ...(isCssValue(width) ? { width } : {}),
    ...(isCssValue(height) ? { height } : {})
  };
};

const createIcon = (Icon: LucideIcon) => {
  const CompatibilityIcon = React.forwardRef<SVGSVGElement, CompatibilityIconProps>(
    ({ sx, style, role, 'aria-hidden': ariaHidden, 'aria-label': ariaLabel, ...props }, ref) => {
      const hasAccessibleName = Boolean(ariaLabel || props['aria-labelledby']);

      return React.createElement(Icon, {
        ...props,
        ref,
        'aria-hidden': ariaHidden ?? (hasAccessibleName ? undefined : true),
        'aria-label': ariaLabel,
        role: role ?? (hasAccessibleName ? 'img' : undefined),
        size: '1em',
        style: { ...toCssStyle(sx), ...style }
      });
    }
  );

  CompatibilityIcon.displayName = Icon.displayName;

  return CompatibilityIcon;
};

export const Add = createIcon(Plus);
export const AddchartOutlined = createIcon(ChartNoAxesCombined);
export const AddToPhotosOutlined = createIcon(Images);
export const ArrowDownward = createIcon(ArrowDown);
export const ArrowDropDown = createIcon(ChevronDown);
export const ArrowUpward = createIcon(ArrowUp);
export const AssistantOutlined = createIcon(Bot);
export const ContentCopyOutlined = createIcon(Copy);
export const DeleteOutlined = createIcon(Trash2);
export const DisplaySettingsIcon = createIcon(Settings2);
export const DragIndicator = createIcon(GripVertical);
export const ExpandMore = createIcon(ChevronDown);
export const FilterList = createIcon(ListFilter);
export const InfoOutlined = createIcon(Info);
export const Launch = createIcon(ExternalLink);
export const ListAltOutlined = createIcon(ListChecks);
export const LockOutlined = createIcon(Lock);
export const Search = createIcon(SearchIcon);
export const ShareOutlined = createIcon(Share2);
export const ThumbDown = createIcon(ThumbsDown);
export const ThumbDownOutlined = createIcon(ThumbsDown);
export const ThumbUp = createIcon(ThumbsUp);
export const ThumbUpOutlined = createIcon(ThumbsUp);
export const UnfoldMore = createIcon(ChevronsUpDown);
export const VpnKeyOutlined = createIcon(KeyRound);
